import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { Search } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { CONNECTIONS_CATALOG } from "@/lib/connectionsCatalog";
import { MEGA_GROUPS, FILTER_PILLS } from "@/lib/connectionsMegaGroups";
import { ConnectionsPipeline } from "@/components/org/connections/ConnectionsPipeline";
import { SetupDrawer } from "@/components/org/connections/SetupDrawer";
import { CT } from "@/lib/connectionsTheme";

export default function OrgConnections() {
  const { API } = useAuth();
  const location = useLocation();
  const [statusMap, setStatusMap] = useState({});
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [activeCapabilityKey, setActiveCapabilityKey] = useState(null);
  const [highlightKey, setHighlightKey] = useState(null);
  const [activeMegaFilter, setActiveMegaFilter] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/org/connections`, { withCredentials: true });
      const next = {};
      (data.connections || []).forEach((c) => {
        next[c.capability_key] = next[c.capability_key] || {};
        next[c.capability_key][c.sub_capability_key] = c;
      });
      setStatusMap(next);
      setCategoryOrder(data.category_order || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoaded(true);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const hash = (location.hash || "").replace("#", "");
    if (hash) handleFilterClick(hash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const handleConnectionSaved = (capabilityKey, subKey, connection) => {
    setStatusMap((prev) => ({
      ...prev,
      [capabilityKey]: { ...(prev[capabilityKey] || {}), [subKey]: connection },
    }));
  };

  const handleReorder = async (nextOrder) => {
    setCategoryOrder(nextOrder);
    try {
      await axios.patch(`${API}/org/connections/category-order`, { order: nextOrder }, { withCredentials: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const activeCapability = CONNECTIONS_CATALOG.find((c) => c.key === activeCapabilityKey) || null;

  const handleFilterClick = (key) => {
    setHighlightKey(key);
    setActiveMegaFilter(null);
    if (key) {
      document.getElementById(`org-connections-anchor-${key}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => setHighlightKey(null), 2000);
    }
  };

  const searchLower = search.trim().toLowerCase();
  const activeGroup = useMemo(() => MEGA_GROUPS.find((g) => g.key === activeMegaFilter) || null, [activeMegaFilter]);
  const orderedCatalog = useMemo(() => {
    if (!categoryOrder.length) return CONNECTIONS_CATALOG;
    const byKey = {};
    CONNECTIONS_CATALOG.forEach((c) => { byKey[c.key] = c; });
    const ordered = categoryOrder.map((k) => byKey[k]).filter(Boolean);
    CONNECTIONS_CATALOG.forEach((c) => { if (!categoryOrder.includes(c.key)) ordered.push(c); });
    return ordered;
  }, [categoryOrder]);
  const reorderDisabled = Boolean(activeGroup) || Boolean(searchLower);
  const visibleCapabilities = useMemo(() => {
    return orderedCatalog.filter((cap) => {
      if (activeGroup && !activeGroup.capabilityKeys.includes(cap.key)) return false;
      if (!searchLower) return true;
      return (
        cap.label.toLowerCase().includes(searchLower) ||
        cap.subtitle.toLowerCase().includes(searchLower) ||
        cap.subCapabilities.some((s) => s.label.toLowerCase().includes(searchLower))
      );
    });
  }, [orderedCatalog, activeGroup, searchLower]);

  return (
    <div className={CT.page} data-testid="org-connections-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className={CT.h1}>Connections</h1>
          <p className={`mt-1 ${CT.textSecondary}`}>Connect your tools, enable customer access, and power your Nova Opsis workflows.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search connections..."
              data-testid="org-connections-search-input"
              className={`${CT.input} w-56 pl-8`}
            />
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="org-connections-filter-bar">
        {FILTER_PILLS.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => setActiveMegaFilter(p.key)}
            data-testid={`org-connections-filter-${p.key || "all"}`}
            className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors duration-150 ${
              activeMegaFilter === p.key ? "bg-blue-600 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-100 dark:border-blue-500/20 dark:text-slate-300 dark:hover:bg-[#0F172A]"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" data-testid="org-connections-status-legend">
        <span className={`${CT.label} normal-case tracking-normal`}>Status:</span>
        <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
          <span className="h-2 w-2 rounded-full bg-emerald-500" /> Active
        </span>
        <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
          <span className="h-2 w-2 rounded-full bg-amber-500" /> Partially Configured
        </span>
        <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
          <span className="h-2 w-2 rounded-full bg-slate-400" /> Not Configured
        </span>
        {!reorderDisabled && (
          <span className="text-slate-400 dark:text-slate-500">· Drag a card to reorder</span>
        )}
      </div>

      {error && <p className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400" data-testid="org-connections-error">{error}</p>}

      {!loaded ? (
        <div className={`mt-6 ${CT.panel} p-10 text-center text-sm font-semibold text-slate-500 dark:text-slate-400`} data-testid="org-connections-loading">
          Loading your Connections…
        </div>
      ) : (
        <div className="mt-6">
          <ConnectionsPipeline
            categories={visibleCapabilities}
            statusMap={statusMap}
            onSetup={setActiveCapabilityKey}
            highlightKey={highlightKey}
            emptyMessage={`No connections match "${search}".`}
            onReorder={handleReorder}
            reorderDisabled={reorderDisabled}
          />
        </div>
      )}

      {activeCapability && (
        <SetupDrawer
          capability={activeCapability}
          statusMap={statusMap}
          onClose={() => setActiveCapabilityKey(null)}
          onConnectionSaved={handleConnectionSaved}
        />
      )}
    </div>
  );
}
